// CanvasDrawingUtils.js - PART 1 of 2
// All canvas drawing primitives (backgrounds, images, overlays, decorations)

// ===== SHADOW HELPERS =====

export const applyShadow = (ctx, shadowConfig) => {
  if (!shadowConfig) {
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    return;
  }
  
  ctx.shadowColor = shadowConfig.color || 'rgba(0, 0, 0, 0.3)';
  ctx.shadowBlur = shadowConfig.blur || 0;
  ctx.shadowOffsetX = shadowConfig.offsetX || 0;
  ctx.shadowOffsetY = shadowConfig.offsetY || 0;
};

export const clearShadow = (ctx) => {
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
};

// ===== IMAGE LOADING & CACHING =====

const imageCache = new Map();

export const loadImage = (url) => {
  return new Promise((resolve, reject) => {
    if (imageCache.has(url)) {
     
      resolve(imageCache.get(url));
      return;
    }

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      imageCache.set(url, img);
      
      resolve(img);
    };
    
    img.onerror = () => {
     
      reject(new Error('Image load failed'));
    };
    
    img.src = url;
  });
};

export const clearImageCache = () => {
  imageCache.clear();
 
};

// ===== IMAGE FILTER FUNCTIONS =====

export const applyImageFilters = (ctx, img, x, y, width, height, filters) => {
  if (!filters) {
    ctx.drawImage(img, x, y, width, height);
    return;
  }

  const tempCanvas = document.createElement('canvas');
  const tempCtx = tempCanvas.getContext('2d');
  tempCanvas.width = width;
  tempCanvas.height = height;
  
  tempCtx.drawImage(img, 0, 0, width, height);
  const imageData = tempCtx.getImageData(0, 0, width, height);
  const data = imageData.data;
  
  // Grayscale
  if (filters.grayscale) {
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      const amount = filters.grayscale;
      data[i] = data[i] * (1 - amount) + gray * amount;
      data[i + 1] = data[i + 1] * (1 - amount) + gray * amount;
      data[i + 2] = data[i + 2] * (1 - amount) + gray * amount;
    }
  }
  
  // Brightness
  if (filters.brightness !== undefined) {
    const brightness = (filters.brightness - 1) * 255;
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.max(0, Math.min(255, data[i] + brightness));
      data[i + 1] = Math.max(0, Math.min(255, data[i + 1] + brightness));
      data[i + 2] = Math.max(0, Math.min(255, data[i + 2] + brightness));
    }
  }
  
  // Contrast
  if (filters.contrast !== undefined) {
    const contrast = filters.contrast;
    const factor = (259 * (contrast * 255 + 255)) / (255 * (259 - contrast * 255));
    for (let i = 0; i < data.length; i += 4) {
      data[i] = Math.max(0, Math.min(255, factor * (data[i] - 128) + 128));
      data[i + 1] = Math.max(0, Math.min(255, factor * (data[i + 1] - 128) + 128));
      data[i + 2] = Math.max(0, Math.min(255, factor * (data[i + 2] - 128) + 128));
    }
  }
  
  // Saturation
  if (filters.saturation !== undefined) {
    const saturation = filters.saturation;
    for (let i = 0; i < data.length; i += 4) {
      const gray = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
      data[i] = Math.max(0, Math.min(255, gray + (data[i] - gray) * saturation));
      data[i + 1] = Math.max(0, Math.min(255, gray + (data[i + 1] - gray) * saturation));
      data[i + 2] = Math.max(0, Math.min(255, gray + (data[i + 2] - gray) * saturation));
    }
  }
  
  tempCtx.putImageData(imageData, 0, 0);
  ctx.drawImage(tempCanvas, x, y, width, height);
};

export const drawRotatedImage = (ctx, img, x, y, width, height, rotation) => {
  if (!rotation || rotation === 0) {
    ctx.drawImage(img, x, y, width, height);
    return;
  }
  
  ctx.save();
  ctx.translate(x + width / 2, y + height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -width / 2, -height / 2, width, height);
  ctx.restore();
};

export const drawGlitchEffect = (ctx, img, x, y, width, height, intensity = 0.3) => {
  const slices = 8;
  const sliceHeight = height / slices;
  
  for (let i = 0; i < slices; i++) {
    const sourceY = (i / slices) * img.height;
    const destY = y + (i * sliceHeight);
    const offset = (Math.random() - 0.5) * intensity * 20;
    
    ctx.drawImage(
      img,
      0, sourceY, img.width, img.height / slices,
      x + offset, destY, width, sliceHeight
    );
  }
};

// ===== OVERLAY FUNCTIONS =====

export const drawGridOverlay = (ctx, width, height, gridConfig) => {
  if (!gridConfig) return;
  
  ctx.save();
  ctx.strokeStyle = gridConfig.color;
  ctx.lineWidth = gridConfig.lineWidth || 1;
  ctx.globalAlpha = gridConfig.opacity || 0.3;
  
  const spacing = gridConfig.spacing || 40;
  
  for (let x = 0; x < width; x += spacing) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x, height);
    ctx.stroke();
  }
  
  for (let y = 0; y < height; y += spacing) {
    ctx.beginPath();
    ctx.moveTo(0, y);
    ctx.lineTo(width, y);
    ctx.stroke();
  }
  
  ctx.restore();
};

export const drawWaveOverlay = (ctx, width, height, waveConfig) => {
  if (!waveConfig) return;
  
  ctx.save();
  ctx.strokeStyle = waveConfig.color || '#ffffff';
  ctx.lineWidth = waveConfig.lineWidth || 2;
  ctx.globalAlpha = waveConfig.opacity || 0.15;
  
  const amplitude = waveConfig.amplitude || 50;
  const frequency = waveConfig.frequency || 0.01;
  const numWaves = waveConfig.numWaves || 3;
  
  for (let w = 0; w < numWaves; w++) {
    ctx.beginPath();
    const yOffset = (height / numWaves) * w;
    
    for (let x = 0; x < width; x += 2) {
      const y = yOffset + Math.sin(x * frequency + w) * amplitude;
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
  }
  
  ctx.restore();
};

export const drawMeshOverlay = (ctx, width, height, meshConfig) => {
  if (!meshConfig) return;
  
  ctx.save();
  ctx.fillStyle = meshConfig.color || '#ffffff';
  ctx.globalAlpha = meshConfig.opacity || 0.08;
  
  const spacing = meshConfig.spacing || 30;
  const dotSize = meshConfig.dotSize || 2;
  
  for (let x = 0; x < width; x += spacing) {
    for (let y = 0; y < height; y += spacing) {
      ctx.beginPath();
      ctx.arc(x, y, dotSize, 0, Math.PI * 2);
      ctx.fill();
    }
  }
  
  ctx.restore();
};

// ===== BACKGROUND DRAWING =====

export const drawBackground = (ctx, dimensions, bgConfig) => {
  const { width, height } = dimensions;
  
  if (bgConfig.type === 'solid') {
    ctx.fillStyle = bgConfig.color;
    ctx.fillRect(0, 0, width, height);
  } 
  else if (bgConfig.type === 'gradient') {
    let gradient;
    
    if (bgConfig.direction === 'vertical') {
      gradient = ctx.createLinearGradient(0, 0, 0, height);
    } else if (bgConfig.direction === 'diagonal') {
      gradient = ctx.createLinearGradient(0, 0, width, height);
    } else if (bgConfig.direction === 'radial') {
      gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, Math.max(width, height)/2);
    } else {
      gradient = ctx.createLinearGradient(0, 0, width, 0);
    }
    
    bgConfig.colors.forEach((color, i) => {
      gradient.addColorStop(i / (bgConfig.colors.length - 1), color);
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);
  }
  else if (bgConfig.type === 'split') {
    ctx.fillStyle = bgConfig.leftColor;
    ctx.fillRect(0, 0, bgConfig.splitAt, height);
    ctx.fillStyle = bgConfig.rightColor;
    ctx.fillRect(bgConfig.splitAt, 0, width - bgConfig.splitAt, height);
  }
};

// ===== DECORATIVE ELEMENTS =====

export const drawGlassCard = (ctx, glassConfig) => {
  if (!glassConfig) return;
  
  ctx.save();
  
  const { x, y, width, height, borderRadius, background, border, shadow } = glassConfig;
  
  applyShadow(ctx, shadow);
  
  ctx.beginPath();
  const r = borderRadius || 0;
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + r);
  ctx.lineTo(x + width, y + height - r);
  ctx.quadraticCurveTo(x + width, y + height, x + width - r, y + height);
  ctx.lineTo(x + r, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  
  ctx.fillStyle = background;
  ctx.fill();
  
  if (border) {
    clearShadow(ctx);
    ctx.strokeStyle = border.color;
    ctx.lineWidth = border.width;
    ctx.stroke();
  }
  
  ctx.restore();
};

export const drawAccentBox = (ctx, accentConfig) => {
  if (!accentConfig) return;
  
  ctx.save();
  applyShadow(ctx, accentConfig.shadow);
  
  ctx.fillStyle = accentConfig.color;
  ctx.fillRect(accentConfig.x, accentConfig.y, accentConfig.width, accentConfig.height);
  
  if (accentConfig.border) {
    clearShadow(ctx);
    ctx.strokeStyle = accentConfig.border.color;
    ctx.lineWidth = accentConfig.border.width;
    ctx.strokeRect(accentConfig.x, accentConfig.y, accentConfig.width, accentConfig.height);
  }
  
  ctx.restore();
};

export const drawDecorativeLine = (ctx, lineConfig) => {
  if (!lineConfig) return;
  
  ctx.save();
  ctx.strokeStyle = lineConfig.color;
  ctx.lineWidth = lineConfig.height || lineConfig.width || 2;
  
  ctx.beginPath();
  ctx.moveTo(lineConfig.x || lineConfig.x1, lineConfig.y || lineConfig.y1);
  ctx.lineTo((lineConfig.x + lineConfig.width) || lineConfig.x2, lineConfig.y || lineConfig.y2);
  ctx.stroke();
  
  ctx.restore();
};

export const drawAccent = (ctx, accentConfig) => {
  if (!accentConfig) return;
  
  ctx.fillStyle = accentConfig.color;
  ctx.strokeStyle = accentConfig.color;
  
  if (accentConfig.type === 'line') {
    ctx.lineWidth = accentConfig.width;
    ctx.beginPath();
    ctx.moveTo(accentConfig.x1, accentConfig.y1);
    ctx.lineTo(accentConfig.x2, accentConfig.y2);
    ctx.stroke();
  }
  else if (accentConfig.type === 'box') {
    ctx.fillRect(accentConfig.x, accentConfig.y, accentConfig.width, accentConfig.height);
  }
};




// ===== MACOS DOTS =====
// ===== MACOS DOTS =====
export const drawMacOSDots = (ctx, dotsConfig) => {
  if (!dotsConfig || !dotsConfig.enabled) return;
  
  ctx.save();
  
  const { x, y, spacing, size, colors } = dotsConfig;
  
  // Draw three circular dots (red, yellow, green)
  colors.forEach((color, index) => {
    const dotX = x + (index * spacing);
    const dotY = y;
    
    // Stronger shadow for bigger dots
    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = 6;
    ctx.shadowOffsetY = 3;
    
    // Draw the dot
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.arc(dotX, dotY, size / 2, 0, Math.PI * 2);
    ctx.fill();
    
    // Glossy highlight effect
    ctx.shadowBlur = 0;
    ctx.shadowOffsetY = 0;
    ctx.fillStyle = 'rgba(255, 255, 255, 0.4)';
    ctx.beginPath();
    ctx.arc(dotX - size / 6, dotY - size / 6, size / 3.5, 0, Math.PI * 2);
    ctx.fill();
  });
  
  ctx.restore();
};

// ===== IMAGE DRAWING =====

export const drawImage = (ctx, img, imageConfig) => {
  if (!img || !imageConfig.enabled) return;

  ctx.save();

  // Calculate aspect ratio fit
  const imgAspect = img.width / img.height;
  const boxAspect = imageConfig.width / imageConfig.height;

  let drawWidth, drawHeight, offsetX, offsetY;

  if (imgAspect > boxAspect) {
    drawHeight = imageConfig.height;
    drawWidth = drawHeight * imgAspect;
    offsetX = imageConfig.x - (drawWidth - imageConfig.width) / 2;
    offsetY = imageConfig.y;
  } else {
    drawWidth = imageConfig.width;
    drawHeight = drawWidth / imgAspect;
    offsetX = imageConfig.x;
    offsetY = imageConfig.y - (drawHeight - imageConfig.height) / 2;
  }

  // Apply shadow
  applyShadow(ctx, imageConfig.shadow);

  // Create clipping path for rounded corners
  if (imageConfig.borderRadius > 0) {
    ctx.beginPath();
    const x = imageConfig.x;
    const y = imageConfig.y;
    const w = imageConfig.width;
    const h = imageConfig.height;
    const r = imageConfig.borderRadius;

    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
    ctx.clip();
  }

  clearShadow(ctx);
  ctx.globalAlpha = imageConfig.opacity || 1;

  // Draw image with effects
  if (imageConfig.glitchEffect) {
    drawGlitchEffect(ctx, img, offsetX, offsetY, drawWidth, drawHeight);
  } else if (imageConfig.rotation) {
    drawRotatedImage(ctx, img, offsetX, offsetY, drawWidth, drawHeight, imageConfig.rotation);
  } else if (imageConfig.filters) {
    applyImageFilters(ctx, img, offsetX, offsetY, drawWidth, drawHeight, imageConfig.filters);
  } else {
    ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
  }

  // Draw border
  if (imageConfig.border) {
    ctx.globalAlpha = 1;
    ctx.strokeStyle = imageConfig.border.color;
    ctx.lineWidth = imageConfig.border.width;
    
    if (imageConfig.borderRadius > 0) {
      ctx.stroke();
    } else {
      ctx.strokeRect(imageConfig.x, imageConfig.y, imageConfig.width, imageConfig.height);
    }
  }

  // Apply overlay
  if (imageConfig.overlay) {
    ctx.globalAlpha = 0.4;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(imageConfig.x, imageConfig.y, imageConfig.width, imageConfig.height);
  }

  ctx.restore();
};

// ===== END OF PART 1 =====
// Continue with Part 2 for Chart Drawing Functions
// CanvasDrawingUtils.js - PART 2 of 2
// ===== CHART DRAWING FUNCTIONS (✅ WITH VISIBLE NUMBERS) =====

// ✅ Helper: Format numbers for display
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};


export const drawBarChart = (ctx, data, config) => {
  const { 
    x, y, width, height, 
    barColors, labels, 
    showGrid = true, 
    showValues = true,
    style = 1,
    isMultiSeries = false,
    series = null
  } = config;
  
  ctx.save();
  
  // ✅ Handle multi-series vs single-series
  let maxValue;
  let dataPointCount;
  let seriesCount = 1;
  
  if (isMultiSeries && series) {
    // Multi-series: find max across all series
    maxValue = Math.max(...series.flatMap(s => s.values));
    dataPointCount = labels.length;
    seriesCount = series.length;
  } else {
    // Single-series
    maxValue = Math.max(...data);
    dataPointCount = data.length;
  }
  
  // ✅ ADAPTIVE BAR WIDTH based on total bar count
  // ✅ IMPROVED: Calculate optimal bar width to fill the chart area
// ✅ PREMIUM: Calculate optimal bar width to fill the chart area beautifully
const totalBars = dataPointCount * seriesCount;
let barWidth;
let groupSpacing;
let barSpacing;

// Calculate base bar width based on available width and number of groups
if (isMultiSeries) {
  // Multi-series: account for multiple bars per group
  barSpacing = seriesCount > 2 ? 15 : 25;
  
  if (totalBars <= 6) {
    // Few bars: PREMIUM thick bars
    const totalBarSpace = width * 0.85; // Use 85% for bars
    barWidth = totalBarSpace / (dataPointCount * seriesCount + dataPointCount * 0.3);
    barWidth = Math.max(100, Math.min(barWidth, 200));
    groupSpacing = 50;
  } else if (totalBars <= 12) {
    const totalBarSpace = width * 0.80;
    barWidth = totalBarSpace / (dataPointCount * seriesCount + dataPointCount * 0.4);
    barWidth = Math.max(70, Math.min(barWidth, 140));
    groupSpacing = 40;
  } else {
    const totalBarSpace = width * 0.75;
    barWidth = totalBarSpace / (dataPointCount * seriesCount + dataPointCount * 0.5);
    barWidth = Math.max(50, Math.min(barWidth, 100));
    groupSpacing = 35;
  }
} else {
  // Single-series: bars can be MUCH THICKER
  if (dataPointCount <= 3) {
    // 🔥 PREMIUM: Very few bars = SUPER THICK with minimal gaps
    barWidth = width * 0.85 / (dataPointCount + 0.5); // Use 85% of width, minimal spacing
    barWidth = Math.max(300, Math.min(barWidth, 500)); // THICK: 300-500px
    groupSpacing = (width - (dataPointCount * barWidth)) / (dataPointCount + 1);
  } else if (dataPointCount <= 5) {
    // Few bars: very thick
    barWidth = width * 0.80 / (dataPointCount + 0.8);
    barWidth = Math.max(200, Math.min(barWidth, 350));
    groupSpacing = (width - (dataPointCount * barWidth)) / (dataPointCount + 1);
  } else if (dataPointCount <= 8) {
    // Medium: thick bars
    barWidth = width * 0.75 / (dataPointCount + 1);
    barWidth = Math.max(140, Math.min(barWidth, 220));
    groupSpacing = (width - (dataPointCount * barWidth)) / (dataPointCount + 1);
  } else if (dataPointCount <= 12) {
    // More bars: balanced
    barWidth = width * 0.70 / (dataPointCount + 1.2);
    barWidth = Math.max(100, Math.min(barWidth, 160));
    groupSpacing = (width - (dataPointCount * barWidth)) / (dataPointCount + 1);
  } else if (dataPointCount <= 18) {
    // Many bars
    barWidth = width * 0.65 / (dataPointCount + 1.5);
    barWidth = Math.max(70, Math.min(barWidth, 120));
    groupSpacing = (width - (dataPointCount * barWidth)) / (dataPointCount + 1);
  } else {
    // Too many bars: compact but readable
    barWidth = width * 0.60 / (dataPointCount + 2);
    barWidth = Math.max(45, Math.min(barWidth, 90));
    groupSpacing = (width - (dataPointCount * barWidth)) / (dataPointCount + 1);
  }
  
  barSpacing = 0; // Not needed for single series
}

// Ensure minimum spacing (but allow tight spacing for few bars)
if (dataPointCount <= 3) {
  groupSpacing = Math.max(40, groupSpacing); // Tighter for premium look
} else {
  groupSpacing = Math.max(30, groupSpacing);
}

const groupWidth = (barWidth * seriesCount) + (barSpacing * (seriesCount - 1));
  
  // ✅ Draw Y-axis scale labels
  if (showGrid) {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.fillStyle = config.textColor || '#ffffff';
    ctx.font = '16px Inter, sans-serif';
    ctx.textAlign = 'right';
    
    for (let i = 0; i <= 5; i++) {
      const gridY = y + (height / 5) * i;
      const value = maxValue * (1 - i / 5);
      
      ctx.beginPath();
      ctx.moveTo(x, gridY);
      ctx.lineTo(x + width, gridY);
      ctx.stroke();
      
      ctx.fillText(formatNumber(Math.round(value)), x - 15, gridY + 5);
    }
  }
  
  // ✅ Draw bars
  for (let groupIndex = 0; groupIndex < dataPointCount; groupIndex++) {
    const groupX = x + groupSpacing + groupIndex * (groupWidth + groupSpacing);
    
    if (isMultiSeries && series) {
      // ✅ MULTI-SERIES: Draw grouped bars
      series.forEach((seriesData, seriesIndex) => {
        const value = seriesData.values[groupIndex];
        const barHeight = (value / maxValue) * height;
        const barX = groupX + seriesIndex * (barWidth + barSpacing);
        const barY = y + height - barHeight;
        
        const baseColor = barColors[seriesIndex % barColors.length];
        
        if (style === 2) {
          // Rounded bars
          const radius = Math.min(barWidth / 2, 15);
          ctx.fillStyle = baseColor;
          ctx.beginPath();
          ctx.moveTo(barX, barY + radius);
          ctx.lineTo(barX, y + height);
          ctx.lineTo(barX + barWidth, y + height);
          ctx.lineTo(barX + barWidth, barY + radius);
          ctx.arcTo(barX + barWidth, barY, barX + barWidth - radius, barY, radius);
          ctx.lineTo(barX + radius, barY);
          ctx.arcTo(barX, barY, barX, barY + radius, radius);
          ctx.closePath();
          ctx.fill();
          
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
          ctx.lineWidth = 2;
          ctx.stroke();
        } else {
          // Gradient bars
          const gradient = ctx.createLinearGradient(barX, barY, barX, barY + barHeight);
          gradient.addColorStop(0, baseColor);
          gradient.addColorStop(1, baseColor + '99');
          ctx.fillStyle = gradient;
          ctx.fillRect(barX, barY, barWidth, barHeight);
          
          ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.lineWidth = 2;
          ctx.strokeRect(barX, barY, barWidth, barHeight);
        }
        
        // Value labels (only if bars are wide enough)
        if (showValues && barWidth >= 40) {
          ctx.fillStyle = config.textColor || '#ffffff';
          ctx.font = totalBars > 12 ? 'bold 20px Inter' : 'bold 28px Inter';
          ctx.textAlign = 'center';
          ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
          ctx.shadowBlur = 4;
          ctx.fillText(formatNumber(value), barX + barWidth / 2, barY - 10);
          ctx.shadowBlur = 0;
        }
      });
      
    } else {
      // ✅ SINGLE-SERIES: Draw one bar per group
      const value = data[groupIndex];
      const barHeight = (value / maxValue) * height;
      const barX = groupX;
      const barY = y + height - barHeight;
      
      const baseColor = barColors[0]; // Takes the FIRST color from the array
      
      if (style === 2) {
        const radius = Math.min(barWidth / 2, 20);
        ctx.fillStyle = baseColor;
        ctx.beginPath();
        ctx.moveTo(barX, barY + radius);
        ctx.lineTo(barX, y + height);
        ctx.lineTo(barX + barWidth, y + height);
        ctx.lineTo(barX + barWidth, barY + radius);
        ctx.arcTo(barX + barWidth, barY, barX + barWidth - radius, barY, radius);
        ctx.lineTo(barX + radius, barY);
        ctx.arcTo(barX, barY, barX, barY + radius, radius);
        ctx.closePath();
        ctx.fill();
        
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
        ctx.lineWidth = 2;
        ctx.stroke();
      } else {
        const gradient = ctx.createLinearGradient(barX, barY, barX, barY + barHeight);
        gradient.addColorStop(0, baseColor);
        gradient.addColorStop(1, baseColor + '99');
        ctx.fillStyle = gradient;
        ctx.fillRect(barX, barY, barWidth, barHeight);
        
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
        ctx.lineWidth = 2;
        ctx.strokeRect(barX, barY, barWidth, barHeight);
      }
      
      if (showValues) {
        ctx.fillStyle = config.textColor || '#ffffff';
        ctx.font = 'bold 28px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4;
        ctx.fillText(formatNumber(value), barX + barWidth / 2, barY - 30);
        ctx.shadowBlur = 0;
      }
    }
    
    // ✅ X-axis labels (centered under each group)
    if (labels && labels[groupIndex]) {
      ctx.fillStyle = config.textColor || '#ffffff';
      ctx.font = totalBars > 12 ? 'bold 20px Inter' : 'bold 28px Inter';
      ctx.textAlign = 'center';
      ctx.fillText(labels[groupIndex], groupX + groupWidth / 2, y + height + 35);
    }
  }
  
  // ✅ Draw legend for multi-series
  if (isMultiSeries && series) {
    const legendY = y - 50;
    let legendX = x;
    
    series.forEach((seriesData, index) => {
      const color = barColors[index % barColors.length];
      
      // Legend box
      ctx.fillStyle = color;
      ctx.fillRect(legendX, legendY, 30, 20);
      
      // Legend text
      ctx.fillStyle = config.textColor || '#ffffff';
      ctx.font = 'bold 20px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(seriesData.name, legendX + 40, legendY + 15);
      
      legendX += ctx.measureText(seriesData.name).width + 100;
    });
  }
  
  ctx.restore();
};


export const drawLineChart = (ctx, data, config) => {
  const { 
    x, y, width, height, 
    barColors,
    pointRadius = 6,
    showGrid = true,
    labels,
    showValues = true,
    style = 1,
    isMultiSeries = false,
    series = null
  } = config;
  
  ctx.save();
  
  // ✅ Handle multi-series vs single-series
  let maxValue, minValue, dataPointCount;
  
  if (isMultiSeries && series) {
    const allValues = series.flatMap(s => s.values);
    maxValue = Math.max(...allValues);
    minValue = Math.min(...allValues);
    dataPointCount = labels.length;
  } else {
    maxValue = Math.max(...data);
    minValue = Math.min(...data);
    dataPointCount = data.length;
  }
  
  const range = maxValue - minValue || 1;
  const pointSpacing = width / (dataPointCount - 1);
  
  // ✅ Draw Y-axis scale
  if (showGrid) {
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;
    ctx.fillStyle = config.textColor || '#ffffff';
    ctx.font = '22px Inter, sans-serif';
    ctx.textAlign = 'right';
    
    for (let i = 0; i <= 5; i++) {
      const gridY = y + (height / 5) * i;
      const value = maxValue - (range * i / 5);
      
      ctx.beginPath();
      ctx.moveTo(x, gridY);
      ctx.lineTo(x + width, gridY);
      ctx.stroke();
      
      ctx.fillText(formatNumber(Math.round(value)), x - 15, gridY + 5);
    }
  }
  
  if (isMultiSeries && series) {
    // ✅ MULTI-SERIES: Draw multiple lines
    series.forEach((seriesData, seriesIndex) => {
      const lineColor = barColors[seriesIndex % barColors.length];
      const fillColor = lineColor + '33';
      
      // Draw filled area (only for style 1)
      if (style === 1) {
        ctx.fillStyle = fillColor;
        ctx.beginPath();
        ctx.moveTo(x, y + height);
        
        seriesData.values.forEach((value, index) => {
          const pointX = x + index * pointSpacing;
          const pointY = y + height - ((value - minValue) / range) * height;
          ctx.lineTo(pointX, pointY);
        });
        
        ctx.lineTo(x + width, y + height);
        ctx.closePath();
        ctx.fill();
      }
      
      // Draw line
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = style === 2 ? 5 : 4;
      ctx.beginPath();
      
      seriesData.values.forEach((value, index) => {
        const pointX = x + index * pointSpacing;
        const pointY = y + height - ((value - minValue) / range) * height;
        
        if (index === 0) {
          ctx.moveTo(pointX, pointY);
        } else {
          ctx.lineTo(pointX, pointY);
        }
      });
      
      ctx.stroke();
      
      // Draw points
      seriesData.values.forEach((value, index) => {
        const pointX = x + index * pointSpacing;
        const pointY = y + height - ((value - minValue) / range) * height;
        
        ctx.fillStyle = config.textColor || '#ffffff';
        ctx.beginPath();
        ctx.arc(pointX, pointY, pointRadius, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = 3;
        ctx.stroke();
      });
    });
    
  } else {
    // ✅ SINGLE-SERIES: Original behavior
    const lineColor = barColors?.[0] || '#3b82f6';
    const fillColor = lineColor + '33';
    
    if (style === 1) {
      ctx.fillStyle = fillColor;
      ctx.beginPath();
      ctx.moveTo(x, y + height);
      
      data.forEach((value, index) => {
        const pointX = x + index * pointSpacing;
        const pointY = y + height - ((value - minValue) / range) * height;
        ctx.lineTo(pointX, pointY);
      });
      
      ctx.lineTo(x + width, y + height);
      ctx.closePath();
      ctx.fill();
    }
    
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = style === 2 ? 5 : 4;
    ctx.beginPath();
    
    data.forEach((value, index) => {
      const pointX = x + index * pointSpacing;
      const pointY = y + height - ((value - minValue) / range) * height;
      
      if (index === 0) {
        ctx.moveTo(pointX, pointY);
      } else {
        ctx.lineTo(pointX, pointY);
      }
    });
    
    ctx.stroke();
    
    data.forEach((value, index) => {
      const pointX = x + index * pointSpacing;
      const pointY = y + height - ((value - minValue) / range) * height;
      
      ctx.fillStyle = config.textColor || '#ffffff';
      ctx.beginPath();
      ctx.arc(pointX, pointY, pointRadius, 0, Math.PI * 2);
      ctx.fill();
      
      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 3;
      ctx.stroke();
      
      if (showValues && dataPointCount <= 8) {
        ctx.fillStyle = config.textColor || '#ffffff';
        ctx.font = 'bold 28px Inter, sans-serif';
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.8)';
        ctx.shadowBlur = 4;
        ctx.fillText(formatNumber(value), pointX,pointY - 35);
        ctx.shadowBlur = 0;
      }
    });
  }
  
  // ✅ X-axis labels
  if (labels) {
    ctx.fillStyle = config.textColor || '#ffffff';
    ctx.font = 'bold 16px Inter, sans-serif';
    ctx.textAlign = 'center';
    
    labels.forEach((label, index) => {
      const pointX = x + index * pointSpacing;
      ctx.fillText(label, pointX, y + height + 30);
    });
  }
  
  // ✅ Draw legend for multi-series
  if (isMultiSeries && series) {
    const legendY = y - 50;
    let legendX = x;
    
    series.forEach((seriesData, index) => {
      const color = barColors[index % barColors.length];
      
      // Legend line
      ctx.strokeStyle = color;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(legendX, legendY + 10);
      ctx.lineTo(legendX + 30, legendY + 10);
      ctx.stroke();
      
      // Legend text
      ctx.fillStyle = config.textColor || '#ffffff';
      ctx.font = 'bold 20px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.fillText(seriesData.name, legendX + 40, legendY + 15);
      
      legendX += ctx.measureText(seriesData.name).width + 100;
    });
  }
  
  ctx.restore();
};


// Add this to CanvasDrawingUtils.js
export const drawFloatingBlobs = (ctx, blobs) => {
  if (!blobs || !Array.isArray(blobs)) return;
  
  blobs.forEach(blob => {
    ctx.save();
    
    // Soft glow shadow
    ctx.shadowColor = blob.color;
    ctx.shadowBlur = blob.blur || 50;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    
    // Semi-transparent fill
    ctx.fillStyle = blob.color;
    ctx.globalAlpha = 0.4; // Adjust for subtlety
    
    ctx.beginPath();
    ctx.arc(blob.x, blob.y, blob.radius, 0, Math.PI * 2);
    ctx.fill();
    
    // Optional: inner glow or lighter center for more "bubble" feel
    const gradient = ctx.createRadialGradient(blob.x, blob.y, 0, blob.x, blob.y, blob.radius);
    gradient.addColorStop(0, 'rgba(255, 255, 255, 0.3)');
    gradient.addColorStop(1, 'transparent');
    ctx.fillStyle = gradient;
    ctx.globalAlpha = 0.3;
    ctx.fill();
    
    ctx.restore();
  });
};